/**
 * Phase 6 — Executive Audit View
 * Permission: EXEC_AUDIT_LIHAT (super_admin, direktur_utama, auditor).
 * Read-only trail dari exec_audit.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft } from "lucide-react";

export default function ExecutiveAuditView() {
  const [filters, setFilters] = useState({ action: "", user_id: "", target: "", date_from: "", date_to: "" });
  const [rows, setRows] = useState([]);
  const [actionsAvail, setActionsAvail] = useState([]);
  const [usersAvail, setUsersAvail] = useState([]);
  const [err, setErr] = useState(null);

  const load = () => {
    setErr(null);
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) q.set(k, v); });
    api.get(`/executive/audit-view?${q.toString()}`)
      .then(({ data }) => {
        setRows(data.items || []);
        setActionsAvail(data.filters?.actions || []);
        setUsersAvail(data.filters?.users || []);
      })
      .catch((e) => {
        if (e?.response?.status === 403) setErr("Tidak berwenang mengakses audit view eksekutif.");
        else setErr(formatApiError(e));
      });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (err) {
    return (
      <div className="p-6 space-y-4" data-testid="exec-audit-page">
        <Link to="/executive-dashboard" className="text-xs text-sky-700 hover:underline inline-flex items-center">
          <ArrowLeft size={12} className="mr-1" />Kembali
        </Link>
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-amber-900 text-sm" data-testid="exec-audit-no-access">
          {err}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="exec-audit-page">
      <Link to="/executive-dashboard" className="text-xs text-sky-700 hover:underline inline-flex items-center">
        <ArrowLeft size={12} className="mr-1" />Kembali ke Dashboard Eksekutif
      </Link>
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Audit Eksekutif</h1>
        <p className="text-xs md:text-sm text-slate-500">
          Catatan perubahan konfigurasi alert, target, acknowledgement, note, dan pembuatan laporan eksekutif. Read-only.
        </p>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Filter</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div>
              <Label className="text-xs">Action</Label>
              <Select value={filters.action || "ALL"} onValueChange={(v) => setFilters((f) => ({ ...f, action: v === "ALL" ? "" : v }))}>
                <SelectTrigger data-testid="audit-filter-action"><SelectValue placeholder="Semua" /></SelectTrigger>
                <SelectContent><SelectItem value="ALL">Semua</SelectItem>
                  {actionsAvail.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">User</Label>
              <Select value={filters.user_id || "ALL"} onValueChange={(v) => setFilters((f) => ({ ...f, user_id: v === "ALL" ? "" : v }))}>
                <SelectTrigger data-testid="audit-filter-user"><SelectValue placeholder="Semua" /></SelectTrigger>
                <SelectContent><SelectItem value="ALL">Semua</SelectItem>
                  {usersAvail.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Target (regex)</Label>
              <Input value={filters.target} onChange={(e) => setFilters((f) => ({ ...f, target: e.target.value }))} placeholder="exec_targets/.*" data-testid="audit-filter-target" />
            </div>
            <div>
              <Label className="text-xs">Dari</Label>
              <Input type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} data-testid="audit-filter-from" />
            </div>
            <div>
              <Label className="text-xs">Sampai</Label>
              <Input type="date" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} data-testid="audit-filter-to" />
            </div>
          </div>
          <div className="mt-3">
            <button type="button" className="text-xs px-3 py-1 border rounded hover:bg-slate-50" onClick={load} data-testid="audit-apply">
              Terapkan Filter
            </button>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Trail ({rows.length} entri)</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Waktu</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Target</TableHead>
              <TableHead>Sebelum</TableHead>
              <TableHead>Sesudah</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-xs text-slate-400 italic py-6">
                  Tidak ada entri audit yang cocok.
                </TableCell></TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`audit-row-${r.id}`}>
                  <TableCell className="text-xs whitespace-nowrap">{new Date(r.at).toLocaleString("id-ID")}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px]">{r.action}</Badge></TableCell>
                  <TableCell className="text-xs">{r.user_id}</TableCell>
                  <TableCell className="text-xs">{r.user_role}</TableCell>
                  <TableCell className="text-xs font-mono">{r.target}</TableCell>
                  <TableCell className="text-xs max-w-[200px]">
                    <details><summary className="cursor-pointer text-slate-500">Lihat</summary>
                      <pre className="text-[10px] whitespace-pre-wrap">{JSON.stringify(r.before, null, 1)}</pre>
                    </details>
                  </TableCell>
                  <TableCell className="text-xs max-w-[200px]">
                    <details><summary className="cursor-pointer text-slate-500">Lihat</summary>
                      <pre className="text-[10px] whitespace-pre-wrap">{JSON.stringify(r.after, null, 1)}</pre>
                    </details>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
